import { makeAutoObservable } from "mobx";
import { ZAttrMeta, zAttrMeta } from "src/business/attribute";
import {
  AttrUserType,
  getAttrUserType,
} from "src/business/attribute/AttrUserType";
import { deleteSubAttr } from "src/business/attribute/deleteSubAttr";
import { onError } from "src/common/onError";
import { toggleSetItem } from "src/common/toggleSetItem";

import {
  AttrEditorV2Context,
  AttrEditorV2Store,
} from "../AttrEditorV2/AttrEditorV2Store";
import { AttrLocation } from "./AttrLocation";
import { AttrRow, buildRows } from "./AttrRow";

export class AttrListV2Store {
  constructor() {
    makeAutoObservable(this, { attrLocation: false });
  }

  attrList: ZAttrMeta[] = [];

  setAttrList(newList: ZAttrMeta[]) {
    this.attrList = newList;
  }

  setAttrListRaw(newList: unknown) {
    if (!newList) {
      this.setAttrList([]);
    } else {
      const res = zAttrMeta.array().safeParse(newList);
      if (res.error) onError(res.error);
      this.setAttrList(res.success ? res.data : []);
    }
  }

  get allRows(): AttrRow[] {
    return buildRows(this.attrList);
  }

  get visibleRows(): AttrRow[] {
    const { search } = this;
    let rows = this.allRows.filter(
      ({ ownerKeys }) =>
        !ownerKeys.find((rowKey) => this.collapsed.has(rowKey)),
    );
    if (search) {
      const lowSearch = matchRule(search);
      const visibleKeys = new Set<string>();
      rows.forEach(({ key, ownerKeys, attr }) => {
        if (isAttrMatch(lowSearch, attr)) {
          visibleKeys.add(key);
          ownerKeys.forEach((k) => visibleKeys.add(k));
        }
      });
      rows = rows.filter(({ key }) => visibleKeys.has(key));
    }
    return rows;
  }

  // Collapse

  collapsed = new Set<string>();

  toggleCollapse(key: string) {
    toggleSetItem(this.collapsed, key);
  }

  get allCollapsibleKeys(): string[] {
    return this.allRows.filter(({ isLeaf }) => !isLeaf).map(({ key }) => key);
  }

  get canCollapseAll(): boolean {
    return this.collapsed.size < this.allCollapsibleKeys.length;
  }

  collapseAll() {
    this.collapsed = new Set(this.allCollapsibleKeys);
    this.search = ""; // чтобы не было конфликта между тем что что-то выделено, но попало в свёрнутое состояние
  }

  get canExpandAll(): boolean {
    return this.collapsed.size > 0;
  }

  expandAll() {
    this.collapsed.clear();
  }

  // Search
  search = "";

  setSearch(newValue: string) {
    this.search = newValue;

    // Если среди свёрнутых строк есть совпадение, то нужно снять коллапс со всех владельцев
    const matchValue = matchRule(newValue);
    const { collapsed } = this;
    this.allRows.forEach(({ attr, ownerKeys }) => {
      if (isAttrMatch(matchValue, attr)) {
        ownerKeys.forEach((ok) => {
          collapsed.delete(ok);
        });
      }
    });
  }

  get emptyText(): string {
    return this.search
      ? "Нет атрибутов, соответствующих условию"
      : "Нет атрибутов";
  }

  // Selection
  selectedRows = new Set<string>();

  clearSelected() {
    this.selectedRows.clear();
  }

  toggleSelection(rowKey: string) {
    toggleSetItem(this.selectedRows, rowKey);
  }

  toggleSelectionAll() {
    const { allRows, selectedRowKeys } = this;
    if (selectedRowKeys.length === allRows.length) {
      this.clearSelected();
    } else {
      this.selectedRows = new Set(allRows.map(({ key }) => key));
    }
  }

  get selectedOwners(): Set<string> {
    const result = new Set<string>();
    const { selectedRows } = this;
    this.allRows.forEach(({ key, ownerKeys }) => {
      if (ownerKeys.find((ownerKey) => selectedRows.has(ownerKey))) {
        result.add(key);
      }
    });
    return result;
  }

  /** Подчинённые атрибуты будут принудительно выделены, если выделен их владелец */
  get selectedRowKeys(): string[] {
    const { selectedOwners, selectedRows } = this;
    return this.allRows
      .map(({ key }) => key)
      .filter((key) => selectedOwners.has(key) || selectedRows.has(key));
  }

  // Delete attributes
  get canDelete(): boolean {
    return !this.deleteMsg;
  }

  get deleteMsg(): string | undefined {
    if (this.selectedRows.size === 0)
      return "Необходимо выбрать хотя бы один атрибут";
    return undefined;
  }

  deleteMode = false;

  setDeleteMode(open: boolean) {
    this.deleteMode = open;
  }

  doDelete(): ZAttrMeta[] {
    const { selectedRows } = this;
    const result: ZAttrMeta[] = [];
    // Сначала оставляем все верхнеуровневые атрибуты, которые не выбраны.
    this.attrList.forEach((attr) => {
      if (!selectedRows.has(attr.uid)) {
        result.push(attr);
      }
    });

    // Теперь нужно модифицировать те атрибуты, из которых удаляются вложенные
    this.allRows.forEach(({ key, attr, ownerKeys }) => {
      if (ownerKeys.length > 0 && selectedRows.has(key)) {
        const topKey = ownerKeys[ownerKeys.length - 1];
        const ownerIndex = result.findIndex(({ uid }) => uid === topKey);
        const owner = result[ownerIndex];
        if (owner) {
          const newOwner = deleteSubAttr(owner, attr);
          if (newOwner) {
            result[ownerIndex] = newOwner;
          }
        }
      }
    });

    this.clearSelected();
    return result;
  }

  // Attributes
  attrLocation: AttrLocation = { type: "newTop" };

  attrStore: AttrEditorV2Store | null = null;

  setAttrStore(store: AttrEditorV2Store | null) {
    this.attrStore = store;
  }

  startEdit(attr: ZAttrMeta, location: AttrLocation, ctx: AttrEditorV2Context) {
    this.attrLocation = location;
    this.setAttrStore(new AttrEditorV2Store(attr, ctx));
  }

  onNewAttr(userType: AttrUserType, owner?: AttrRow) {
    const newAttr = userType.create();
    newAttr.uid = generateUid();
    const location: AttrLocation = owner
      ? { type: "newSub", owner }
      : { type: "newTop" };
    const ctx = {
      isNew: true,
      usedNames: this.getUsedNamesForOwner(owner),
      ownerName: owner?.attr.title,
    } satisfies AttrEditorV2Context;
    this.startEdit(newAttr, location, ctx);
  }

  onEditAttr(row: AttrRow) {
    const ctx = {
      isNew: false,
      // тут слегка перестарался, т.к. в данном случае поле имени всё равно запрещено для изменения. Но пусть будет.
      usedNames: this.getUsedNamesForRow(row),
    } satisfies AttrEditorV2Context;
    this.startEdit(row.attr, { type: "edit", row }, ctx);
  }

  getOwnerRow(row: AttrRow | undefined): AttrRow | undefined {
    const ownerKey = row?.ownerKeys[0];
    return ownerKey
      ? this.allRows.find(({ key }) => key === ownerKey)
      : undefined;
  }

  getUsedNamesForRow(row: AttrRow | undefined): string[] {
    return this.getUsedNamesForOwner(this.getOwnerRow(row), row?.key);
  }

  getUsedNamesForOwner(
    owner: AttrRow | undefined,
    exceptKey?: string,
  ): string[] {
    const { allRows } = this;
    const rows = exceptKey
      ? allRows.filter(({ key }) => key !== exceptKey)
      : allRows;
    const levelRows: AttrRow[] = rows.filter(
      ({ ownerKeys }) => ownerKeys[0] === owner?.key,
    );
    return levelRows.map(({ attr }) => attr.name);
  }

  /**
   * Применить изменения, полученные из редактора атрибута.
   * @param attr
   */
  updateAttr(attr: ZAttrMeta): ZAttrMeta[] {
    const { attrLocation } = this;
    const result = [...this.attrList];
    const editTopAttr = () => {
      const pos = result.findIndex(({ uid }) => attr.uid === uid);
      if (pos >= 0) result[pos] = attr;
    };
    const onSubAttr = (ownerKey: string, cmd: "edit" | "new") => {
      const ownerRow = this.allRows.find(({ key }) => key === ownerKey);
      if (ownerRow) {
        const ownerType = ownerRow.attr.type;
        if (ownerType.name === "list") {
          if (cmd === "edit") {
            ownerType.fields = ownerType.fields?.map((it) =>
              it.uid === attr.uid ? attr : it,
            );
          } else if (cmd === "new") {
            ownerType.fields = ownerType.fields ?? [];
            ownerType.fields.push(attr);
          }
        }
      }
    };
    switch (attrLocation.type) {
      case "newTop":
        result.push(attr);
        break;
      case "newSub":
        onSubAttr(attrLocation.owner.key, "new");
        break;
      case "edit":
        if (attrLocation.row.ownerKeys.length === 0) {
          // Обновить верхнеуровневый атрибут
          editTopAttr();
        } else {
          // Вложенный атрибут
          onSubAttr(attrLocation.row.ownerKeys[0]!, "edit");
        }
        break;
      default:
        break;
    }
    this.setAttrStore(null);
    return result;
  }
}

let uidGen = 0;
const generateUid = (): string => `@${++uidGen}`;

const matchRule = (subString: string) => subString.toLowerCase();

const isPropMatch = (
  subString: string,
  propValue: number | string | null | undefined,
): boolean => {
  if (!subString || propValue === null || propValue === undefined) {
    return false;
  }
  return matchRule(String(propValue)).includes(subString);
};

const isAttrMatch = (subString: string, attr: ZAttrMeta) => {
  const { name, title, displayId } = attr;
  return (
    isPropMatch(subString, name) ||
    isPropMatch(subString, title) ||
    isPropMatch(subString, displayId) ||
    isPropMatch(subString, getAttrUserType(attr)?.name)
  );
};
